import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { RoundingStrategySelect } from '../../components/settings/RoundingStrategySettings'
import { SettingItem, SettingsCard } from '../../components/settings/SettingsCard'
import RoundingStrategy = Models.RoundingStrategy
import { axiosPatch } from '../../util/api'
import { setCurrentUser } from '../../actions/user'
import { Toasts } from '../../components/toaster/Toast'
import { useDispatch } from 'react-redux'

const options: (keyof RoundingStrategy)[] = [
    'bulk_actions_rounding_strategy',
    'recipes_rounding_strategy',
    'smart_bids_rounding_strategy',
    'promotions_rounding_strategy',
]

interface RoundingStrategySettingsProps {
    selectedProfile: Models.Profile
}

const getRoundingStrategies = (settings: Models.Settings | null): RoundingStrategy => ({
    bulk_actions_rounding_strategy: settings?.bulk_actions_rounding_strategy ?? 3,
    recipes_rounding_strategy: settings?.recipes_rounding_strategy ?? 3,
    smart_bids_rounding_strategy: settings?.smart_bids_rounding_strategy ?? 3,
    promotions_rounding_strategy: settings?.promotions_rounding_strategy ?? 3,
})

export const RoundingStrategySettings = ({ selectedProfile }: RoundingStrategySettingsProps) => {
    const dispatch = useDispatch()
    const settings = selectedProfile.settings as Models.Settings | null
    const roundingStrategies = useMemo(() => getRoundingStrategies(settings), [settings])
    const [loading, setLoading] = useState(false)
    const [temp, setTemp] = useState<RoundingStrategy>({
        bulk_actions_rounding_strategy: roundingStrategies.bulk_actions_rounding_strategy,
        recipes_rounding_strategy: roundingStrategies.recipes_rounding_strategy,
        smart_bids_rounding_strategy: roundingStrategies.smart_bids_rounding_strategy,
        promotions_rounding_strategy: roundingStrategies.promotions_rounding_strategy,
    })

    useEffect(() => {
        setTemp(roundingStrategies)
    }, [roundingStrategies])

    const dirty = useMemo(() => {
        return (
            temp.bulk_actions_rounding_strategy !==
                roundingStrategies.bulk_actions_rounding_strategy ||
            temp.recipes_rounding_strategy !== roundingStrategies.recipes_rounding_strategy ||
            temp.smart_bids_rounding_strategy !== roundingStrategies.smart_bids_rounding_strategy ||
            temp.promotions_rounding_strategy !== roundingStrategies.promotions_rounding_strategy
        )
    }, [temp, roundingStrategies])

    const onChange = (name: keyof RoundingStrategy) => (e: ChangeEvent<any>) => {
        const v = e.target.value as string
        setTemp((p) => ({
            ...p,
            [name]: parseInt(v),
        }))
    }

    const output = options.map((option) => {
        return (
            <div key={option} className="flex items-end">
                <RoundingStrategySelect onChange={onChange(option)} value={temp[option]} />
            </div>
        )
    })

    const saveRoundingStrategies = () => {
        setLoading(true)
        axiosPatch(`settings/${settings?.id}/account`, temp)
            .then((res) => {
                setLoading(false)
                dispatch(setCurrentUser(res.data))
                Toasts.success('Rounding strategies settings saved')
            })
            .catch(() => {
                setLoading(false)
            })
    }

    const roundingStrategiesDescription = (
        <span>
            These strategies in Merch Jar determine how we round bid changes that result in
            fractions of a cent. We recommend Weighted Rounding for its balanced handling of these
            adjustments. For a more in-depth understanding of each strategy,
            <a
                className="text-mjBlue-500"
                href="https://help.merchjar.com/blog/rounding-strategies"
                target="_blank"
                rel="noreferrer"
            >
                &nbsp;click here
            </a>
            .
        </span>
    )

    return (
        <SettingsCard
            title={'Rounding Strategies'}
            onSave={dirty ? saveRoundingStrategies : null}
            loading={loading}
            description={roundingStrategiesDescription}
        >
            <SettingItem
                title="Bulk Actions"
                description="Select a strategy for rounding fractional cent changes resulting from Bulk Actions."
                rightComponent={output[0]}
            />
            <SettingItem
                title="Recipes"
                description="Select a strategy for rounding fractional cent changes resulting from Recipes."
                rightComponent={output[1]}
            />
            <SettingItem
                title="Smart Campaigns"
                description="Select a strategy for rounding fractional cent changes resulting from Smart Campaigns."
                rightComponent={output[2]}
            />
            <SettingItem
                title="Smart Bids+"
                description="Select a strategy for rounding fractional cent changes resulting from Smart Bids+."
                rightComponent={output[3]}
                bottomDivider={false}
            />
        </SettingsCard>
    )
}
